import { useProfile } from '@shared/hooks/useProfile';
import { collection, limit, onSnapshot, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../config/firebase';
import { Message } from '../types';

interface LastMessageData {
  lastMessage: Message | null;
  unreadCount: number;
}

export const useLastMessages = () => {
  const [lastMessages, setLastMessages] = useState<Record<string, LastMessageData>>({});
  const { user } = useProfile();

  useEffect(() => {
    if (!user?.id) return;

    // Get all conversations for current user
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.id)
    );

    // Subscribe to conversations to get their IDs
    const unsubscribeConversations = onSnapshot(conversationsQuery, (conversationsSnapshot) => {
      // For each conversation, subscribe to its last message
      const unsubscribers = conversationsSnapshot.docs.map(conversationDoc => {
        const conversationId = conversationDoc.id;

        // Query for unread messages
        const unreadQuery = query(
          collection(db, 'conversations', conversationId, 'messages'),
          where('read', '==', false),
          where('senderId', '!=', user.id)
        );

        // Query for last message
        const lastMessageQuery = query(
          collection(db, 'conversations', conversationId, 'messages'),
          orderBy('timestamp', 'desc'),
          limit(1)
        );

        // Subscribe to both queries
        const unsubscribeUnread = onSnapshot(unreadQuery, (unreadSnapshot) => {
          const unreadCount = unreadSnapshot.size;
          setLastMessages(prev => ({
            ...prev,
            [conversationId]: {
              ...prev[conversationId],
              unreadCount
            }
          }));
        });

        const unsubscribeLastMessage = onSnapshot(lastMessageQuery, (messageSnapshot) => {
          const lastMessage = messageSnapshot.docs[0];
          if (lastMessage) {
            const messageData = {
              id: lastMessage.id,
              ...lastMessage.data(),
              timestamp: (lastMessage.data().timestamp as Timestamp).toDate(),
              type: lastMessage.data().type || 'text',
              mediaUrl: lastMessage.data().mediaUrl
            } as Message;

            setLastMessages(prev => ({
              ...prev,
              [conversationId]: {
                ...prev[conversationId],
                lastMessage: messageData
              }
            }));
          }
        });

        // Return cleanup function
        return () => {
          unsubscribeUnread();
          unsubscribeLastMessage();
        };
      });

      // Cleanup function to unsubscribe from all message listeners
      return () => {
        unsubscribers.forEach(unsubscribe => unsubscribe());
      };
    });

    // Cleanup function to unsubscribe from conversations listener
    return () => {
      unsubscribeConversations();
    };
  }, [user?.id]);

  return lastMessages;
};
